/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, January 18th 2023, 8:29:22 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

interface IMenuItem {
  icon: React.ReactNode;
  text: React.ReactNode;
  onClick: () => void;
  renderMenuItem?: () => React.ReactNode;
}

interface PopupMenuProps extends Pick<React.ComponentProps<typeof Menu>, "anchorOrigin"> {
  menuItems: IMenuItem[];
  tooltipText?: React.ReactNode;
  renderOpenButton?: ({
    handleOpen,
  }: {
    handleOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }) => React.ReactNode;
}

export const PopupMenu = ({ menuItems, renderOpenButton, anchorOrigin, tooltipText }: PopupMenuProps) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openComponent = renderOpenButton?.({ handleOpen: handleClick }) || (
    <IconButton onClick={handleClick}>
      <MoreHorizIcon />
    </IconButton>
  );

  return (
    <>
      {tooltipText ? (
        <Tooltip title={tooltipText}>
          <span>{openComponent}</span>
        </Tooltip>
      ) : (
        openComponent
      )}
      <Menu
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        MenuListProps={{
          "aria-labelledby": "popup-menu-actions",
        }}
        anchorOrigin={
          anchorOrigin ?? {
            vertical: "bottom",
            horizontal: isRtl ? "right" : "left",
          }
        }
      >
        {menuItems.map((menuItem, index) => (
          <MenuItem
            onClick={() => {
              menuItem.onClick?.();
              handleClose();
            }}
            key={`${menuItem.text}-${index}`}
          >
            {menuItem?.renderMenuItem?.() || (
              <>
                <ListItemIcon>{menuItem.icon}</ListItemIcon>
                <ListItemText>{menuItem.text}</ListItemText>
              </>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
